import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { searchParamTools } from 'common/tools/search-param-tools';
import wsCookies from 'common/tools/ws-cookies';
import {
  MakeModelsParams,
  SearchParams,
  searchParamsInitialState,
  StrArrayParams,
  StrParams,
  ZipCodeObjectParams,
} from 'common/types/SearchParams';
import _ from 'lodash';

import { RootState } from './root-reducer';

export interface ZipCodeObject {
  code: string;
  city: string;
  state: string;
  latitude: number;
  longitude: number;
}

interface SearchParamState {
  searchParams: SearchParams;
  urlParams: string;
  searchChanged: boolean;
}

const initialState: SearchParamState = {
  searchParams: searchParamsInitialState,
  urlParams: searchParamTools.toUrlParams(searchParamsInitialState),
  searchChanged: false,
};

const starOff = (value?: string) => {
  if (!value) return undefined;
  return value === '*' ? '' : value;
};

const starOn = (value?: string) => {
  if (!value?.length) return '*';
  return value;
};

const searchParamSlice = createSlice({
  name: 'searchParam',
  initialState,
  reducers: {
    setStrKey(state, action: PayloadAction<{ key: keyof StrParams; val?: string }>) {
      if (!action.payload.key?.length) delete state.searchParams[action.payload.key];
      else state.searchParams[action.payload.key] = action.payload.val;
      state.searchParams.page = 0;
      state.urlParams = searchParamTools.toUrlParams(state.searchParams);
      state.searchChanged = true;
    },
    setArrayKey(state, action: PayloadAction<{ key: keyof StrArrayParams; val?: string[] }>) {
      if (!action.payload.val?.length) delete state.searchParams[action.payload.key];
      else state.searchParams[action.payload.key] = action.payload.val;
      state.searchParams.page = 0;
      state.urlParams = searchParamTools.toUrlParams(state.searchParams);
      state.searchChanged = true;
    },
    addArrayKey(state, action: PayloadAction<{ key: keyof StrArrayParams; val: string }>) {
      const ary = _.clone(state.searchParams[action.payload.key] || []);
      if (!ary.find(x => x === action.payload.val)) ary.push(action.payload.val);
      state.searchParams[action.payload.key] = _.orderBy(ary);
      state.searchParams.page = 0;
      state.urlParams = searchParamTools.toUrlParams(state.searchParams);
      state.searchChanged = true;
    },
    removeArrayKey(state, action: PayloadAction<{ key: keyof StrArrayParams; val: string }>) {
      let ary = state.searchParams[action.payload.key] ? _.clone(state.searchParams[action.payload.key]) : [];
      if (ary?.length) ary = ary.filter(x => x !== action.payload.val);
      if (!ary?.length) delete state.searchParams[action.payload.key];
      else state.searchParams[action.payload.key] = ary;
      state.searchParams.page = 0;
      state.urlParams = searchParamTools.toUrlParams(state.searchParams);
      state.searchChanged = true;
    },
    makeModels(state, action: PayloadAction<MakeModelsParams[] | undefined>) {
      if (!action.payload?.length) delete state.searchParams.makeModels;
      else state.searchParams.makeModels = action.payload;
      state.searchParams.page = 0;
      state.urlParams = searchParamTools.toUrlParams(state.searchParams);
      state.searchChanged = true;
    },
    mileage(state, action: PayloadAction<string | undefined>) {
      const value = starOff(action.payload);
      if (!value?.length) delete state.searchParams.mileage;
      else state.searchParams.mileage = value;
      state.searchParams.page = 0;
      state.urlParams = searchParamTools.toUrlParams(state.searchParams);
      state.searchChanged = true;
    },
    page(state, action: PayloadAction<number>) {
      state.searchParams.page = action.payload;
      state.urlParams = searchParamTools.toUrlParams(state.searchParams);
      state.searchChanged = true;
    },
    rowsPP(state, action: PayloadAction<number>) {
      state.searchParams.rowsPP = action.payload;
      state.searchParams.page = 0;
      state.urlParams = searchParamTools.toUrlParams(state.searchParams);
      state.searchChanged = true;
    },
    sortIdx(state, action: PayloadAction<string>) {
      state.searchParams.sortIdx = action.payload;
      state.searchParams.page = 0;
      state.urlParams = searchParamTools.toUrlParams(state.searchParams);
      state.searchChanged = true;
    },
    priceMin(state, action: PayloadAction<string | undefined>) {
      if (!action.payload?.length) delete state.searchParams.priceMin;
      else {
        const priceMin = action.payload;
        const priceMax = state.searchParams.priceMax;
        state.searchParams.priceMin = action.payload;

        if (priceMin?.length && priceMax?.length && +priceMin > +priceMax) delete state.searchParams.priceMax;
      }
      state.searchParams.page = 0;
      state.urlParams = searchParamTools.toUrlParams(state.searchParams);
      state.searchChanged = true;
    },
    priceMax(state, action: PayloadAction<string | undefined>) {
      if (!action.payload?.length) delete state.searchParams.priceMax;
      else {
        const priceMin = state.searchParams.priceMin;
        const priceMax = action.payload;
        state.searchParams.priceMax = action.payload;
        if (priceMin?.length && priceMax?.length && +priceMin > +priceMax) delete state.searchParams.priceMin;
      }
      state.searchParams.page = 0;
      state.urlParams = searchParamTools.toUrlParams(state.searchParams);
      state.searchChanged = true;
    },
    yearMin(state, action: PayloadAction<string | undefined>) {
      const value = starOff(action.payload);
      if (!value?.length) delete state.searchParams.yearMin;
      else {
        const yearMin = value;
        const yearMax = state.searchParams.yearMax;
        state.searchParams.yearMin = value;
        if (yearMin?.length && yearMax?.length && +yearMin > +yearMax) delete state.searchParams.yearMax;
      }
      state.searchParams.page = 0;
      state.urlParams = searchParamTools.toUrlParams(state.searchParams);
      state.searchChanged = true;
    },
    yearMax(state, action: PayloadAction<string | undefined>) {
      const value = starOff(action.payload);
      if (!value?.length) delete state.searchParams.yearMax;
      else {
        const yearMin = state.searchParams.yearMin;
        const yearMax = value;
        state.searchParams.yearMax = value;
        if (yearMin?.length && yearMax?.length && +yearMin > +yearMax) state.searchParams.yearMin = undefined;
      }
      state.searchParams.page = 0;
      state.urlParams = searchParamTools.toUrlParams(state.searchParams);
      state.searchChanged = true;
    },
    distance(state, action: PayloadAction<string | undefined>) {
      const value = action.payload;
      if (!value?.length) {
        delete state.searchParams.distance;
        wsCookies.remove('distance');
      } else {
        state.searchParams.distance = value;
        wsCookies.set('distance', value);
      }
      state.searchParams.page = 0;
      state.urlParams = searchParamTools.toUrlParams(state.searchParams);
      state.searchChanged = true;
    },
    zipCodeObj(state, action: PayloadAction<ZipCodeObjectParams | undefined>) {
      state.searchParams.zipCodeObj = action.payload;
      state.searchParams.page = 0;
      state.urlParams = searchParamTools.toUrlParams(state.searchParams);
      state.searchChanged = true;
    },
    set(state, action: PayloadAction<SearchParams>) {
      state.searchParams = action.payload;
      state.urlParams = searchParamTools.toUrlParams(state.searchParams);
      state.searchChanged = false;
    },
    searchChanged(state, action: PayloadAction<boolean>) {
      state.searchChanged = action.payload;
    },
    reset(state) {
      state.searchParams = {
        ...searchParamsInitialState,
        zipCodeObj: state.searchParams.zipCodeObj,
        distance: state.searchParams.distance,
      };
      state.urlParams = searchParamTools.toUrlParams(state.searchParams);
      state.searchChanged = true;
    },
  },
});

export const searchParam = searchParamSlice.reducer;
export const searchParamActions = {
  ...searchParamSlice.actions,
};

const rootState = (state: RootState) => state;
const searchParamSliceState = createSelector(rootState, state => state.searchParam);
export const searchParamSelectors = {
  searchParams: createSelector(searchParamSliceState, state => state.searchParams),
  urlParams: createSelector(searchParamSliceState, state => state.urlParams),
  searchChanged: createSelector(searchParamSliceState, state => state.searchChanged),
  bodys: createSelector(searchParamSliceState, state => state.searchParams.bodys),
  colors: createSelector(searchParamSliceState, state => state.searchParams.colors),
  conditions: createSelector(searchParamSliceState, state => state.searchParams.conditions),
  cylinders: createSelector(searchParamSliceState, state => state.searchParams.cylinders),
  doors: createSelector(searchParamSliceState, state => state.searchParams.doors),
  drivetrains: createSelector(searchParamSliceState, state => state.searchParams.drivetrains),
  fuels: createSelector(searchParamSliceState, state => state.searchParams.fuels),
  makeModels: createSelector(searchParamSliceState, state => state.searchParams.makeModels),
  mileage: createSelector(searchParamSliceState, state => state.searchParams.mileage),
  mileageF: createSelector(searchParamSliceState, state => starOn(state.searchParams.mileage)),
  page: createSelector(searchParamSliceState, state => state.searchParams.page),
  priceMax: createSelector(searchParamSliceState, state => state.searchParams.priceMax || ''),
  priceMin: createSelector(searchParamSliceState, state => state.searchParams.priceMin || ''),
  rowsPP: createSelector(searchParamSliceState, state => state.searchParams.rowsPP),
  sortIdx: createSelector(searchParamSliceState, state => state.searchParams.sortIdx),
  titleSs: createSelector(searchParamSliceState, state => state.searchParams.titleSs),
  transmissions: createSelector(searchParamSliceState, state => state.searchParams.transmissions),
  yearMax: createSelector(searchParamSliceState, state => state.searchParams.yearMax),
  yearMin: createSelector(searchParamSliceState, state => state.searchParams.yearMin),
  yearMaxF: createSelector(searchParamSliceState, state => starOn(state.searchParams.yearMax)),
  yearMinF: createSelector(searchParamSliceState, state => starOn(state.searchParams.yearMin)),
  // distance: createSelector(searchParamSliceState, state => state.searchParams.distance),
  distance: createSelector(searchParamSliceState, state => state.searchParams.distance),
  zipCodeObj: createSelector(searchParamSliceState, state => state.searchParams.zipCodeObj),
  filterCount: createSelector(searchParamSliceState, state => {
    return (
      (state.searchParams.bodys?.length || 0) +
      (state.searchParams.colors?.length || 0) +
      (state.searchParams.conditions?.length || 0) +
      (state.searchParams.cylinders?.length || 0) +
      (state.searchParams.doors?.length || 0) +
      (state.searchParams.drivetrains?.length || 0) +
      (state.searchParams.fuels?.length || 0) +
      (state.searchParams.makeModels?.length || 0) +
      (state.searchParams.mileage?.length || 0) +
      (state.searchParams.priceMax?.length ? 1 : 0) +
      (state.searchParams.priceMin?.length ? 1 : 0) +
      (state.searchParams.titleSs?.length || 0) +
      (state.searchParams.yearMax?.length ? 1 : 0) +
      (state.searchParams.yearMin?.length ? 1 : 0) +
      (state.searchParams.transmissions?.length || 0)
    );
  }),
};
